import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const ROLES = ["OWNER", "ADMIN", "ANALYST", "VIEWER"];

export default function WorkspacePage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [pf, setPf] = useState({ name: "", client_ref: "", description: "" });
  const [member, setMember] = useState({ email: "", role: "ANALYST" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadOrg = useCallback((oid) => {
    if (!oid) return;
    api.get(`/workspaces/${oid}`).then((r) => setOrgDetail(r.data)).catch(() => setOrgDetail(null));
    api.get(`/workspaces/${oid}/portfolios`).then((r) => setPortfolios(r.data.results)).catch(() => setPortfolios([]));
  }, []);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/logowanie"); return; }
    api.get("/workspaces").then((r) => {
      setOrgs(r.data.results);
      if (r.data.results.length > 0) setOrgId((cur) => cur || r.data.results[0].id);
    }).catch(() => setOrgs([]));
  }, [user, navigate]);

  useEffect(() => { loadOrg(orgId); }, [orgId, loadOrg]);

  if (!user || orgs === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const errText = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.code) return `${d.code}${d.limit != null ? ` (${d.current}/${d.limit})` : ""}`;
    return t("ws.error");
  };

  const createOrg = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/workspaces", { name: orgName });
      setOrgs([...orgs, { ...r.data, my_role: "OWNER", members: 1 }]);
      setOrgId(r.data.id); setOrgName("");
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const createPortfolio = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/workspaces/${orgId}/portfolios`, pf);
      setPf({ name: "", client_ref: "", description: "" });
      loadOrg(orgId);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const addMember = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/workspaces/${orgId}/members`, member);
      setMember({ email: "", role: "ANALYST" });
      loadOrg(orgId);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const removeMember = async (uid) => {
    setErr(null);
    try { await api.delete(`/workspaces/${orgId}/members/${uid}`); loadOrg(orgId); }
    catch (e) { setErr(errText(e)); }
  };

  const canManage = ["OWNER", "ADMIN"].includes(orgDetail?.my_role);
  const canWrite = ["OWNER", "ADMIN", "ANALYST"].includes(orgDetail?.my_role);

  return (
    <div data-testid="workspace-page" className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="font-heading text-2xl font-bold text-slate-900">{t("ws.title")}</h1>

      {orgs.length === 0 ? (
        <div className="mt-6 max-w-lg rounded-xl border bg-white p-6">
          <p className="text-sm text-slate-600">{t("ws.no_org")}</p>
          <div className="mt-4 flex gap-2">
            <input data-testid="create-org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)}
              placeholder={t("ws.org_name")} className="flex-1 rounded-md border px-3 py-2 text-sm" />
            <Button data-testid="create-org-button" onClick={createOrg} disabled={busy || orgName.trim().length < 2}
              className="bg-[#DC143C] text-white hover:bg-[#B91C1C]">{t("ws.create")}</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {orgs.map((o) => (
              <button key={o.id} data-testid={`org-tab-${o.id}`} onClick={() => setOrgId(o.id)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${o.id === orgId ? "border-slate-900 bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
                {o.name} · {o.my_role}
              </button>
            ))}
            <span className="flex items-center gap-1">
              <input data-testid="create-org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                placeholder={t("ws.org_name")} className="w-40 rounded-md border px-2 py-1 text-xs" />
              <Button data-testid="create-org-button" variant="outline" size="sm" onClick={createOrg}
                disabled={busy || orgName.trim().length < 2}>{t("ws.create")}</Button>
            </span>
          </div>

          {err && <p data-testid="workspace-error" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.portfolios")}</h2>
              <div className="mt-3 space-y-3">
                {portfolios.map((p) => (
                  <Link key={p.id} to={`/portfolio/${p.id}`} data-testid={`portfolio-card-${p.id}`}
                    className="block rounded-xl border bg-white p-4 transition-shadow hover:shadow-md">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      <span className="text-xs text-slate-400">{p.status}</span>
                    </div>
                    {p.client_ref && <p className="mt-0.5 text-xs text-slate-500">{t("ws.client_ref")}: {p.client_ref}</p>}
                    <p className="mt-2 text-xs text-slate-600">
                      {t("ws.parcels_count")}: <b>{p.parcels}</b> · {t("ws.alerts_new")}: <b className={p.unreviewed_alerts ? "text-[#DC143C]" : ""}>{p.unreviewed_alerts}</b>
                    </p>
                  </Link>
                ))}
                {portfolios.length === 0 && <p className="text-sm text-slate-500">{t("ws.no_portfolios")}</p>}
              </div>

              {canWrite && (
                <div className="mt-4 rounded-xl border bg-white p-4">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.create_portfolio")}</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    <input data-testid="create-portfolio-name" value={pf.name} placeholder={t("ws.pf_name")}
                      onChange={(e) => setPf({ ...pf, name: e.target.value })} className="rounded-md border px-3 py-2 text-sm" />
                    <input data-testid="create-portfolio-client" value={pf.client_ref} placeholder={t("ws.pf_client")}
                      onChange={(e) => setPf({ ...pf, client_ref: e.target.value })} className="rounded-md border px-3 py-2 text-sm" />
                    <input data-testid="create-portfolio-desc" value={pf.description} placeholder={t("ws.pf_desc")}
                      onChange={(e) => setPf({ ...pf, description: e.target.value })} className="rounded-md border px-3 py-2 text-sm" />
                  </div>
                  <Button data-testid="create-portfolio-button" onClick={createPortfolio} disabled={busy || pf.name.trim().length < 2}
                    className="mt-3 bg-[#DC143C] text-white hover:bg-[#B91C1C]">{t("ws.create")}</Button>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.members")}</h2>
              <div className="mt-3 rounded-xl border bg-white p-4">
                {(orgDetail?.members || []).map((m) => (
                  <div key={m.user_id} data-testid={`member-row-${m.user_id}`} className="flex items-center justify-between border-b border-slate-100 py-2 text-xs last:border-0">
                    <span className="text-slate-700">{m.email || m.user_id}</span>
                    <span className="flex items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{m.role}</span>
                      {canManage && m.role !== "OWNER" && (
                        <button data-testid={`member-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)}
                          className="text-rose-600 hover:underline">{t("ws.remove")}</button>
                      )}
                    </span>
                  </div>
                ))}
                {canManage && (
                  <div className="mt-3 flex gap-2">
                    <input data-testid="add-member-email" value={member.email} placeholder={t("ws.member_email")}
                      onChange={(e) => setMember({ ...member, email: e.target.value })}
                      className="w-0 flex-1 rounded-md border px-2 py-1.5 text-xs" />
                    <select data-testid="add-member-role" value={member.role}
                      onChange={(e) => setMember({ ...member, role: e.target.value })}
                      className="rounded-md border px-2 py-1.5 text-xs">
                      {ROLES.filter((r) => r !== "OWNER").map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Button data-testid="add-member-button" size="sm" variant="outline" onClick={addMember}
                      disabled={busy || !member.email.includes("@")}>{t("ws.add")}</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
